import {
  PROCESS_STEP_LOGIC_OUTCOME,
  PROCESS_STEP_LOGIC_RANGE,
  PROCESS_STEP_LOGIC_RESPONSE_TYPES,
  PROCESS_STEP_RESPONSE_OPTIONS,
  PROCESS_STEP_RESPONSE_VALUE_TYPES
} from "../data"
import { toId } from "../utils"

function createSuccess() {
  return {
    hasRequirements: false,
    messages: []
  }
}

/**
 * Checks if a given value matches the specified rule according to the logic range.
 *
 * @param {Object} rule - The rule object containing logic range and comparison values.
 * @param {string|number} value - The value to be checked against the rule.
 * @returns {boolean} True if the value matches the rule, false otherwise.
 */
function checkValueMatchesNumberRule(rule, value) {
  const numericValue = parseFloat(value)

  if (Number.isNaN(numericValue)) {
    return false
  }

  switch (rule.range) {
    case PROCESS_STEP_LOGIC_RANGE.BETWEEN:
      if (rule.lowerValue > rule.upperValue) {
        return numericValue >= rule.upperValue && numericValue <= rule.lowerValue
      }
      return numericValue >= rule.lowerValue && numericValue <= rule.upperValue
    case PROCESS_STEP_LOGIC_RANGE.NOT_BETWEEN:
      if (rule.lowerValue > rule.upperValue) {
        return numericValue < rule.upperValue || numericValue > rule.lowerValue
      }
      return numericValue < rule.lowerValue || numericValue > rule.upperValue
    case PROCESS_STEP_LOGIC_RANGE.EQUAL:
      return numericValue === rule.lowerValue

    case PROCESS_STEP_LOGIC_RANGE.NOT_EQUAL:
      return numericValue !== rule.lowerValue

    case PROCESS_STEP_LOGIC_RANGE.GREATER_THAN:
      return numericValue > rule.lowerValue

    case PROCESS_STEP_LOGIC_RANGE.GREATER_OR_EQUAL:
      return numericValue >= rule.lowerValue

    case PROCESS_STEP_LOGIC_RANGE.LESS_THAN:
      return numericValue < rule.lowerValue

    case PROCESS_STEP_LOGIC_RANGE.LESS_OR_EQUAL:
      return numericValue <= rule.lowerValue

    default:
      throw new Error(`Unsupported logic range: ${rule.range}`)
  }
}

/**
 * Checks if a given value matches the specified rule according to the logic range, and rule text values.
 *
 * @param {Object} rule
 * @param {string} value
 * @returns {boolean} True if the value matches the rule, false otherwise.
 */
function checkValueMatchesTextRule(rule, value) {
  // No value, no match
  if (!value) {
    return false
  }

  switch (rule.range) {
    case PROCESS_STEP_LOGIC_RANGE.EQUAL:
      // Handle array/multiple values
      if (Array.isArray(value)) {
        return value.some((v) => rule.textValues.includes(v))
      }

      // Handle single value
      return rule.textValues.includes(value)

    case PROCESS_STEP_LOGIC_RANGE.NOT_EQUAL:
      // Handle array/multiple values
      if (Array.isArray(value)) {
        return value.every((v) => !rule.textValues.includes(v))
      }

      // Handle single value
      return !rule.textValues.includes(value)

    default:
      throw new Error(`Unsupported logic range: ${rule.range}`)
  }
}

/**
 * Delegates to the appropriate function based on the response type associated value type.
 *
 * @param {string} responseType
 * @param {Object} rule
 * @param {string|number} value
 * @returns {boolean} True if the value matches the rule, false otherwise.
 */
function checkValueMatchesRule(responseType, rule, value) {
  const responseOption = PROCESS_STEP_RESPONSE_OPTIONS.find((item) => item.type === responseType)
  if (!responseOption) {
    throw new Error(`Unsupported response type: ${responseType}`)
  }

  switch (responseOption.valueType) {
    case PROCESS_STEP_RESPONSE_VALUE_TYPES.NUMBER:
      return checkValueMatchesNumberRule(rule, value)
    case PROCESS_STEP_RESPONSE_VALUE_TYPES.TEXT:
      return checkValueMatchesTextRule(rule, value)
    default:
      throw new Error(`Unsupported value type: ${responseOption.valueType} for response type: ${responseType}`)
  }
}

/**
 * Checks if the logic outcomes are satisfied for a given rule and step.
 *
 * @param {Object} rule
 * @param {Object} step
 * @param {string|number} newResponse
 * @returns {Object} An object containing the hasRequirement, message, and type.
 */
function checkLogicOutcomesSatisfied(rule, step, newResponse) {
  if (!rule) {
    return createSuccess()
  }

  if (!PROCESS_STEP_LOGIC_RESPONSE_TYPES.includes(step.responseType)) {
    return createSuccess()
  }

  const ruleMatch = checkValueMatchesRule(step.responseType, rule, newResponse)

  if (!ruleMatch) {
    return createSuccess()
  }

  // return status of outcomes
  const outcomes = rule.outcomes.map((outcome) => {
    // Nothing required for show steps outcome
    if (outcome.type === PROCESS_STEP_LOGIC_OUTCOME.SHOW_STEPS) {
      return { hasRequirement: false }
    }

    if (outcome.type === PROCESS_STEP_LOGIC_OUTCOME.ACTION) {
      if (step.unresolvedActions?.length > 0) {
        return { hasRequirement: false }
      }
      return {
        hasRequirement: true,
        message: "An action needs to be raised for this step",
        type: outcome.type
      }
    }
    if (outcome.type === PROCESS_STEP_LOGIC_OUTCOME.NOTE) {
      if (step.notes?.length > 0) {
        return { hasRequirement: false }
      }
      return {
        hasRequirement: true,
        message: "A note needs to be added for this step",
        type: outcome.type
      }
    }
    if (outcome.type === PROCESS_STEP_LOGIC_OUTCOME.NOTE_TEXT) {
      if (step.notes?.length > 0 && step.notes?.some((note) => note.text != null && note.text.length > 0)) {
        return { hasRequirement: false }
      }
      return {
        hasRequirement: true,
        message: "A text note needs to be added for this step",
        type: outcome.type
      }
    }
    if (outcome.type === PROCESS_STEP_LOGIC_OUTCOME.NOTE_IMAGE) {
      if (
        step.notes?.length > 0 &&
        step.notes?.some(
          (note) => note.uploads.length > 0 && note.uploads.some((upload) => upload.fileGroup === "image")
        )
      ) {
        return { hasRequirement: false }
      }
      return {
        hasRequirement: true,
        message: "An image note needs to be added for this step",
        type: outcome.type
      }
    }
    if (outcome.type === PROCESS_STEP_LOGIC_OUTCOME.NOTE_FILE) {
      if (step.notes?.length > 0 && step.notes?.some((note) => note.uploads.length > 0)) {
        return { hasRequirement: false }
      }
      return {
        hasRequirement: true,
        message: "An file note needs to be added for this step",
        type: outcome.type
      }
    }
    throw new Error(`Unsupported logic outcome: ${outcome.type}`)
  })

  return {
    hasRequirements: outcomes.some((outcome) => outcome.hasRequirement),
    messages: outcomes.map((outcome) => outcome.message).filter((message) => message),
    types: outcomes.map((outcome) => outcome.type).filter((type) => type)
  }
}

function checkLogic(step, newResponse) {
  const result = {
    hasRequirements: false,
    messages: [],
    types: []
  }

  if (!step.logic || step.logic.length === 0) {
    return result
  }

  if (!PROCESS_STEP_LOGIC_RESPONSE_TYPES.includes(step.responseType)) {
    return result
  }

  for (const rule of step.logic) {
    const { hasRequirements, messages, types } = checkLogicOutcomesSatisfied(rule, step, newResponse)

    if (hasRequirements) {
      result.hasRequirements = true
      result.messages.push(...messages)
      result.types.push(...types)
    }
  }

  return result
}

/**
 * Calculate the not applicable state map for a step. Used for optimistic response / local state updates.
 *
 * @param {Job} job
 * @param {JobProcessStep} step
 * @param {string} value
 * @returns {Map<string, { step: string, notApplicable: boolean }>} A map of step ids to not applicable states.
 */
function calculateNotApplicableStateMap(job, step, value) {
  // No logic for step
  if (!step.logic || step.logic.length === 0) {
    return
  }

  const nextState = new Map()

  // Run through logic
  for (const logic of step.logic) {
    // Check if value matches logic

    const valueMatchesLogic = checkValueMatchesRule(step.responseType, logic, value)

    for (const outcome of logic.outcomes) {
      // Only process show steps outcomes
      if (outcome.type !== PROCESS_STEP_LOGIC_OUTCOME.SHOW_STEPS) {
        continue
      }

      // Show steps
      const notApplicable = !valueMatchesLogic

      for (const outcomeStep of outcome.steps) {
        nextState.set(toId(outcomeStep), {
          step: toId(outcomeStep),
          notApplicable
        })
      }
    }
  }

  // No state updates needed
  if (nextState.size === 0) {
    return
  }

  // Set the job step id for each state update
  for (const jobStep of job.status.steps) {
    const key = toId(jobStep.step)
    if (!nextState.has(key)) {
      continue
    }

    // Set job step id
    nextState.set(key, {
      ...nextState.get(key),
      id: jobStep.id,
      typename: jobStep.__typename
    })
  }

  return nextState
}

export { checkLogic, checkValueMatchesRule, calculateNotApplicableStateMap }
